import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Zap, Shield, Truck } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setFeaturedProducts(response.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative bg-[#09090b] text-white min-h-[90vh] flex items-center overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1764349721422-e69e54f3e15d?crop=entropy&cs=srgb&fm=jpg&q=85)' 
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-transparent to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-2 bg-[#00ff9d] text-black font-bold text-xs uppercase tracking-widest rounded-full mb-6">
              GOAttitude
            </span>
            <h1 
              className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tight leading-none mb-6"
              data-testid="hero-title"
            >
              Be Your Own <br />
              <span className="text-[#00ff9d]">G.O.A.T.</span>
            </h1>
            <p className="text-lg md:text-xl text-zinc-300 max-w-xl mb-10 leading-relaxed">
              Performance gear built for young athletes who want to push harder, jump higher, and level up.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/shop" 
                className="btn-accent inline-flex items-center justify-center gap-2 group"
                data-testid="shop-now-btn"
              >
                Shop Now
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link 
                to="/about" 
                className="btn-secondary border-white text-white hover:bg-white hover:text-black inline-flex items-center justify-center"
                data-testid="learn-more-btn"
              >
                Learn More
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Strip */}
      <section className="bg-[#00ff9d] py-6">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Zap, text: 'Performance Engineered' },
              { icon: Shield, text: 'Premium Quality' },
              { icon: Truck, text: 'Australia-Wide Shipping' }
            ].map((item, index) => (
              <div key={index} className="flex items-center justify-center gap-3 py-2">
                <item.icon className="w-5 h-5 text-black" />
                <span className="font-bold text-black uppercase tracking-wider text-sm">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 md:py-32 bg-white" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-[#ff4d6d] font-bold text-sm uppercase tracking-widest">Featured</span>
            <h2 className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tight mt-2">
              Performance Essentials
            </h2>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="bg-zinc-100 aspect-square rounded-none" />
                  <div className="mt-4 h-4 bg-zinc-100 rounded w-3/4" />
                  <div className="mt-2 h-4 bg-zinc-100 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link 
                    to={`/product/${product.id}`}
                    className="group block"
                    data-testid={`featured-product-${index}`}
                  >
                    <div className="relative aspect-square bg-zinc-100 overflow-hidden card-hover">
                      <img
                        src={product.imageUrl}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="mt-4">
                      <h3 className="font-heading font-bold text-lg uppercase tracking-tight">
                        {product.name}
                      </h3>
                      <p className="text-zinc-500 text-sm mt-1 line-clamp-1">
                        {product.description}
                      </p>
                      <p className="font-bold text-lg mt-2">
                        ${product.price.toFixed(2)} <span className="text-zinc-400 text-sm font-normal">AUD</span>
                      </p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link 
              to="/shop" 
              className="btn-primary inline-flex items-center gap-2 group"
              data-testid="view-all-btn"
            >
              View All Products
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#09090b] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading font-black text-4xl md:text-5xl uppercase tracking-tight mb-6">
              Ready to Level Up?
            </h2>
            <p className="text-zinc-400 text-lg max-w-xl mx-auto mb-10">
              Join the G.O.A.T. movement. Performance gear for athletes who refuse to settle.
            </p>
            <Link 
              to="/shop" 
              className="btn-accent inline-flex items-center gap-2 neon-glow group"
              data-testid="cta-shop-btn"
            >
              Start Shopping
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
